import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Send, MessageCircle, User, X, Info } from 'lucide-react';

// Estrai API Base dal .env o usa fallback
const API_BASE = process.env.REACT_APP_API_URL || 'https://api-chat.easycasaitaly.it/api/v1';
const SIGNUP_URL = process.env.REACT_APP_SIGNUP_URL || '';
const LOGIN_URL = process.env.REACT_APP_LOGIN_URL || '';
const IS_STANDALONE = typeof window !== 'undefined' && window.self === window.top;

// ── Profili brand ───────────────────────────────────────────────────────────
// Il frontend è condiviso tra tutti i brand: il vestito (nome, testi, palette,
// domini iframe) è selezionato a build-time via REACT_APP_BRAND. Default easycasa
// per retrocompatibilità: senza l'arg il build resta identico al chatbot host.
const BRAND = process.env.REACT_APP_BRAND || 'easycasa';
const BRANDS = {
  easycasa: {
    tag: 'easycasa',
    docTitle: 'EasyCasa — Assistente Host',
    title: 'Carla · Assistente Host',
    subtitle: 'EasyCasa Italy AI',
    welcome: 'Ciao! Sono Carla, l’assistente di EasyCasa Italy. Vuoi sapere come ottimizzare il tuo affitto breve o hai dubbi sulle ultime normative?',
    placeholder: 'Chiedimi della cedolare secca o delle tasse...',
    poweredBy: 'Powered by EasyCasa Italy AI',
    authReturn: 'Se hai completato l’accesso a EasyCasa, clicca qui sotto per entrare nella chat AI.',
    ownDomains: ['easycasaitaly.it', 'www.easycasaitaly.it', 'easycasaitaly.com', 'www.easycasaitaly.com'],
    colors: { primary: '#335C4A', primaryDark: '#203a2e', accent: '#e8c97a', accentDark: '#0a2e1e' },
  },
  cdi: {
    tag: 'cdi',
    docTitle: 'Circuito Donne Imprenditrici — Consulente AI',
    title: 'Venere · Consulente AI',
    subtitle: 'Circuito Donne Imprenditrici',
    welcome: 'Ciao! Sono Venere, il Consulente AI del Circuito. Ti aiuto a farti trovare e vendere meglio, a decidere prezzi e preventivi con ordine e ad arrivare preparata dal commercialista o dal consulente. Da dove partiamo?',
    placeholder: 'Chiedimi di prezzi, clienti o di come prepararti dal commercialista...',
    poweredBy: 'Powered by Circuito Donne Imprenditrici',
    authReturn: 'Se hai completato l’accesso, clicca qui sotto per entrare nella chat AI.',
    ownDomains: ['donneimprenditrici.it', 'www.donneimprenditrici.it', 'circuito.donneimprenditrici.it'],
    colors: { primary: '#A10672', primaryDark: '#7a0556', accent: '#EAED41', accentDark: '#3a0228' },
  },
  osservatoriobbcc: {
    tag: 'osservatoriobbcc',
    docTitle: 'Osservatorio BbCc — Assistente AI',
    title: 'Paola · Assistente AI',
    subtitle: 'Osservatorio BbCc',
    logo: '/osservatoriobbcc-marchio-crema.png',
    welcome: 'Buongiorno, sono Paola, l’assistente dell’Osservatorio BbCc. Posso aiutarti su associazioni e APS, bandi, norme per musei e comunicazione digitale. Su che cosa ti serve una mano?',
    placeholder: 'Chiedimi di marketing culturale, eventi o bandi...',
    poweredBy: 'Powered by Osservatorio BbCc',
    authReturn: 'Se hai completato l’accesso, clicca qui sotto per entrare nella chat AI.',
    ownDomains: ['osservatorio.bbcc.it', 'www.osservatorio.bbcc.it', 'staging.osservatorio.bbcc.it'],
    colors: { primary: '#9b2226', primaryDark: '#100d0b', accent: '#d8a54a', accentDark: '#100d0b' },
  },
};
const B = BRANDS[BRAND] || BRANDS.easycasa;
const C = B.colors;
const OWN_DOMAINS = B.ownDomains;
const IS_OWN_DOMAIN_PARENT = (() => {
  if (typeof document === 'undefined' || !document.referrer) return false;
  try { return OWN_DOMAINS.includes(new URL(document.referrer).hostname.toLowerCase()); }
  catch (e) { return false; }
})();
const BYPASS_BLOCK = (() => {
  if (typeof window === 'undefined') return false;
  try {
    if (new URLSearchParams(window.location.search).get('ok') === '1') return true;
    if (window.sessionStorage && window.sessionStorage.getItem('ec_auth_bypass') === '1') return true;
  } catch (e) { /* ignore */ }
  return false;
})();
// Flag persistente: settato al primo click su Iscriviti/Accedi. Sopravvive alla
// navigation top-frame perché è in localStorage (sessionStorage muore quando
// l'iframe viene distrutto durante il redirect a WP login).
const EC_AUTH_ATTEMPTED = (() => {
  if (typeof window === 'undefined') return false;
  try { return window.localStorage && window.localStorage.getItem('ec_auth_attempted') === '1'; }
  catch (e) { return false; }
})();
const SHOW_AUTH_BANNER = !BYPASS_BLOCK && (IS_STANDALONE || IS_OWN_DOMAIN_PARENT) && (SIGNUP_URL || LOGIN_URL);

function App() {
  const [messages, setMessages] = useState([
    { id: 'welcome', role: 'assistant', content: B.welcome }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(`session_${Math.random().toString(36).substr(2, 9)}`);

  const scrollRef = useRef(null);

  useEffect(() => { document.title = B.docTitle; }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMsg = { id: Date.now(), role: 'user', content: input };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    // S83: notifica al parent (iframe host, es. easycasaitaly.com) che l'utente
    // ha inviato un messaggio. Usato per metering quota lato dashboard cliente
    // (PMPro su .com legge questo e incrementa il counter mensile).
    // Emesso PRIMA dell'axios: conta anche timeout/errori (l'utente l'ha
    // comunque "speso"). Solo role='user', mai role='assistant'.
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({
          type: 'ec_chat_message_sent',
          brand: B.tag,
          timestamp: Date.now(),
          role: 'user'
        }, '*');
      }
    } catch (_) { /* iframe sandbox o cross-origin block: ignora */ }

    try {
      const res = await axios.post(`${API_BASE}/chatbot/query`, {
        session_id: sessionId,
        query: input,
        history: messages.map(m => ({ role: m.role, content: m.content }))
      });

      setMessages(prev => [...prev, {
        id: res.data.conversation_id,
        role: 'assistant',
        content: res.data.answer
      }]);

    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Ops, ho un problema di connessione. Riprova tra poco!' }]);
    } finally {
      setLoading(false);
    }
  };

  // ── BLOCK SCREEN (login/signup required) ─────────────────────────────────
  // Due step distinti per dare feedback chiaro al cliente:
  //   step 1 (rosso, BLOCCATO)    → primo accesso, nessun tentativo registrato
  //   step 2 (verde, PASSO 2 DI 2) → l'utente ha già cliccato Iscriviti/Accedi e
  //                                  sta tornando dopo la sessione WP
  if (SHOW_AUTH_BANNER) {
    const isStep2 = EC_AUTH_ATTEMPTED;
    const markAttempted = () => { try { window.localStorage.setItem("ec_auth_attempted", "1"); } catch (_) {} };
    const enterChat = () => {
      try { window.sessionStorage.setItem("ec_auth_bypass", "1"); } catch (_) {}
      try { window.localStorage.removeItem("ec_auth_attempted"); } catch (_) {}
      window.location.reload();
    };
    const restartFlow = () => {
      try { window.localStorage.removeItem("ec_auth_attempted"); } catch (_) {}
      window.location.reload();
    };
    return (
      <div className={`flex flex-col h-screen max-h-[800px] w-full max-w-2xl mx-auto bg-white shadow-2xl rounded-3xl overflow-hidden border font-sans ${isStep2 ? 'border-emerald-200' : 'border-red-200'}`}>
        <header className={`p-6 flex items-center justify-between shadow-lg ${isStep2 ? 'bg-emerald-600' : 'bg-red-600'}`}>
          <div className="flex items-center gap-3">
            <div className="bg-white/15 p-2 rounded-xl">
              <MessageCircle className="text-white" size={20} />
            </div>
            <div>
              <h1 className="text-white font-bold text-lg leading-tight">{B.title}</h1>
              <p className="text-white/70 text-[10px] uppercase font-black tracking-widest">
                {isStep2 ? 'Quasi fatto' : 'Accesso richiesto'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-white rounded-full"></span>
            <span className="text-[10px] text-white opacity-80 font-bold">
              {isStep2 ? 'PASSO 2 DI 2' : 'BLOCCATO'}
            </span>
          </div>
        </header>

        <div className={`flex-1 flex flex-col items-center justify-center p-8 ${isStep2 ? 'bg-emerald-50' : 'bg-red-50'}`}>
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-lg ${isStep2 ? 'bg-emerald-600' : 'bg-red-600'}`}>
            <span className="text-white text-3xl" aria-hidden="true">{isStep2 ? '✅' : '🔒'}</span>
          </div>
          <h2 className={`text-2xl font-black text-center mb-2 leading-tight ${isStep2 ? 'text-emerald-900' : 'text-red-900'}`}>
            {isStep2 ? 'Hai effettuato l’accesso?' : 'Per usare la chat AI iscriviti gratis'}
          </h2>
          <p className={`text-sm text-center mb-8 max-w-sm leading-relaxed ${isStep2 ? 'text-emerald-700' : 'text-red-700'}`}>
            {isStep2
              ? B.authReturn
              : 'Account Free gratuito · 2 minuti · senza carta di credito.'}
          </p>

          {isStep2 ? (
            <div className="w-full max-w-sm space-y-3">
              <button type="button" onClick={enterChat}
                      className="block w-full bg-emerald-600 hover:bg-emerald-700 text-white text-center py-3.5 rounded-xl font-bold shadow-lg shadow-emerald-900/20 transition-all">
                Entra nella chat &rarr;
              </button>
              <button type="button" onClick={restartFlow}
                      className="block w-full bg-white border-2 border-emerald-200 hover:border-emerald-400 text-emerald-700 text-center py-3 rounded-xl font-bold transition-all">
                Non sono riuscito ad accedere
              </button>
            </div>
          ) : (
            <div className="w-full max-w-sm space-y-3">
              {SIGNUP_URL && (
                <a href={SIGNUP_URL} target="_top"
                   onClick={(e) => { markAttempted(); try { window.sessionStorage.setItem("ec_auth_bypass", "1"); } catch (_) {} try { (window.top || window).location.href = SIGNUP_URL; e.preventDefault(); } catch (_) {} }}
                   className="block w-full bg-red-600 hover:bg-red-700 text-white text-center py-3.5 rounded-xl font-bold shadow-lg shadow-red-900/20 transition-all">
                  Iscriviti gratis &rarr;
                </a>
              )}
              {LOGIN_URL && (
                <a href={LOGIN_URL} target="_top"
                   onClick={(e) => { markAttempted(); try { window.sessionStorage.setItem("ec_auth_bypass", "1"); } catch (_) {} try { (window.top || window).location.href = LOGIN_URL; e.preventDefault(); } catch (_) {} }}
                   className="block w-full bg-white border-2 border-red-200 hover:border-red-400 text-red-700 text-center py-3 rounded-xl font-bold transition-all">
                  Hai già un account? Accedi
                </a>
              )}
            </div>
          )}

          {!isStep2 && (
            <button type="button" onClick={enterChat}
                    className="mt-6 text-xs text-red-600 hover:text-red-800 underline">
              Sei già registrato e loggato? Sblocca la chat
            </button>
          )}
        </div>

        <footer className={`p-4 border-t bg-white ${isStep2 ? 'border-emerald-100' : 'border-red-100'}`}>
          <p className="text-[10px] text-gray-400 text-center">{B.poweredBy}</p>
        </footer>
      </div>
    );
  }

  // ── MAIN CHAT SCREEN ────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-screen max-h-[800px] w-full max-w-2xl mx-auto bg-white shadow-2xl rounded-3xl overflow-hidden border border-gray-100 font-sans">
      {/* HEADER */}
      <header className="p-6 flex items-center justify-between shadow-lg" style={{ backgroundColor: C.primary }}>
        <div className="flex items-center gap-3">
          {B.logo ? (
            <img src={B.logo} alt={B.subtitle} className="h-11 w-11 rounded-xl object-contain" />
          ) : (
          <div className="p-2 rounded-xl" style={{ backgroundColor: C.accent }}>
             <MessageCircle size={20} style={{ color: C.accentDark }} />
          </div>
          )}
          <div>
            <h1 className="text-white font-bold text-lg leading-tight">{B.title}</h1>
            <p className="text-[10px] uppercase font-black tracking-widest" style={{ color: C.accent, opacity: 0.7 }}>{B.subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
            <span className="text-[10px] text-white opacity-60 font-bold">ONLINE</span>
        </div>
      </header>

      {/* CHAT MESSAGES */}
      <main ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-50/30">
        {messages.map(m => (
          <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[85%] p-4 rounded-2xl shadow-sm ${
                m.role === 'user'
                ? 'bg-white text-[#4A4A4A] rounded-tr-none border border-gray-100'
                : 'text-white rounded-tl-none shadow-md'
              }`}
              style={m.role === 'user' ? undefined : { backgroundColor: C.primary }}
            >
               {m.role === 'assistant' ? (
                 <div className="chat-markdown text-sm leading-relaxed">
                   <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                 </div>
               ) : (
                 <p className="text-sm leading-relaxed">{m.content}</p>
               )}
            </div>
          </div>
        ))}
        {loading && (
           <div className="flex justify-start">
             <div className="p-4 rounded-2xl rounded-tl-none flex gap-1" style={{ backgroundColor: `${C.primary}1a` }}>
                <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ backgroundColor: C.primary }}></span>
                <span className="w-1.5 h-1.5 rounded-full animate-bounce [animation-delay:-0.15s]" style={{ backgroundColor: C.primary }}></span>
                <span className="w-1.5 h-1.5 rounded-full animate-bounce [animation-delay:-0.3s]" style={{ backgroundColor: C.primary }}></span>
             </div>
           </div>
        )}
      </main>

      {/* INPUT */}
      <footer className="p-6 bg-white border-t border-gray-100">
        <form onSubmit={handleSend} className="flex items-center gap-3 bg-gray-50 p-2 rounded-2xl border border-gray-200 transition-all">
          <input
            type="text"
            placeholder={B.placeholder}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="flex-1 bg-transparent border-none outline-none p-3 text-sm"
          />
          <button type="submit" className="text-white p-3 rounded-xl transition-all shadow-md" style={{ backgroundColor: C.primary }}>
            <Send size={18} />
          </button>
        </form>
        <p className="text-[10px] text-gray-300 mt-4 text-center">L'AI può commettere errori. Verifica sempre con un esperto ufficiale.</p>
      </footer>
    </div>
  );
}

export default App;
